import { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { getAssetManager, type InstantiatedModel } from "../../systems/AssetManager";
import { tagAs } from "../PlaceholderTag";

export interface ConservatoryBurrowOptions {
  floorY: number;
  shadowGenerator: ShadowGenerator;
  offsetX?: number;
  rack?: boolean;
}

/** Grounded optional root shelter; quest overlap and occupancy stay in OpossumNest. */
export class ConservatoryBurrowVisuals {
  readonly ready: Promise<void>;
  private readonly mount: TransformNode;
  private readonly fallback: AbstractMesh[] = [];
  private readonly fallbackMaterials: PBRMaterial[] = [];
  private model: InstantiatedModel | null = null;
  private casters: AbstractMesh[] = [];
  private lens: PBRMaterial;
  private occupied = false;
  private disposed = false;
  private light: PointLight | null = null;

  constructor(private readonly scene: Scene, private readonly root: TransformNode,
    private readonly options: ConservatoryBurrowOptions) {
    this.mount = new TransformNode(root.name + "_burrowSupport", scene);
    this.mount.parent = root; this.mount.position.y = options.floorY - root.position.y;
    this.mount.position.x = options.offsetX ?? 0;
    const bark = this.fallbackMaterial("bark", new Color3(.16, .09, .045));
    const litter = this.fallbackMaterial("litter", new Color3(.08, .11, .035));
    this.lens = this.fallbackMaterial("lens", new Color3(.12, .22, .11));
    const box = (name: string, x: number, y: number, z: number, w: number, h: number, d: number, mat: PBRMaterial) => {
      const m = MeshBuilder.CreateBox(root.name + "_burrowFallback_" + name, { width: w, height: h, depth: d }, scene);
      m.parent = this.mount; m.position.set(x, y, z); m.material = mat;
      m.isPickable = false; m.receiveShadows = true; tagAs(m, "placeholder"); this.fallback.push(m); return m;
    };
    box("bedding", 0, .045, -1.15, 1.48, .09, .9, litter);
    if (options.rack) {
      box("rackShelf", 0, -.07, -1.21, 2.08, .14, 1.5, bark);
      for (const x of [-.76, .76]) box("rackPlate", x, -.29, -.77, .16, .55, .07, bark);
    }
    for (const x of [-.76, .76]) box("foot", x, .07, -1.15, .19, .14, .7, bark);
    for (let i = 0; i < 8; i++) {
      const angle = (i + .5) * Math.PI / 8;
      const m = box("arch" + i, .78 * Math.cos(angle), .14 + .66 * Math.sin(angle), -1.15, .32, .14, .7, bark);
      m.rotation.z = angle + Math.PI / 2;
    }
    box("status", .68, .58, -.60, .08, .12, .08, this.lens);
    this.registerCasters(this.fallback); this.ready = this.load();
  }

  private fallbackMaterial(name: string, color: Color3): PBRMaterial {
    const material = new PBRMaterial(this.root.name + "_burrowFallback_" + name, this.scene);
    material.albedoColor = color; material.roughness = .95; material.metallic = 0;
    this.fallbackMaterials.push(material); return material;
  }

  private async load(): Promise<void> {
    try {
      const model = await getAssetManager().instantiateModel(this.scene,
        "/assets/models/environment/conservatory_root_burrow.glb", undefined, { doNotInstantiate: true });
      if (this.disposed || this.scene.isDisposed) { model.dispose(); return; }
      const required = ["Burrow_shell", "Burrow_bedding", "Burrow_status_lens"].map(name =>
        model.meshes.find(m => m.name.endsWith(name) && m.getTotalVertices() > 0));
      const lens = required[2]?.material;
      const rack = model.meshes.filter(m => m.name.includes("Burrow_rack_mount"));
      if (required.some(m => !m) || !(lens instanceof PBRMaterial) || (this.options.rack && !rack.some(m => m.getTotalVertices() > 0))) {
        model.dispose(); throw new Error("Root burrow is missing shelter, bedding or occupancy lens");
      }
      for (const node of model.rootNodes) { node.parent = this.mount; tagAs(node, "final"); }
      for (const part of rack) part.setEnabled(!!this.options.rack);
      for (const mesh of model.meshes) { mesh.isPickable = false; mesh.receiveShadows = true; tagAs(mesh, "final"); }
      this.clearCasters(); this.registerCasters(model.meshes);
      for (const mesh of this.fallback) mesh.isVisible = false;
      this.model = model; this.lens = lens; this.applyState();
    } catch (error) {
      if (!this.disposed) console.warn("[ConservatoryBurrowVisuals] Keeping supported shelter fallback:", error);
    }
  }

  setOccupied(occupied: boolean): void { this.occupied = occupied; this.applyState(); }

  private applyState(): void {
    if (this.disposed) return;
    this.lens.emissiveColor.set(this.occupied ? .025 : 0, this.occupied ? .14 : 0, this.occupied ? .05 : 0);
    if (this.occupied && !this.light) {
      this.light = new PointLight("burrowOccupied_" + this.root.name, this.mount.position.clone(), this.scene);
      this.light.parent = this.mount; this.light.position.set(.68, .58, -.55);
      this.light.diffuse = new Color3(.35, .55, .24); this.light.specular = Color3.Black();
      this.light.intensity = .18; this.light.range = 1.5;
    }
    if (this.light) {
      this.light.intensity = this.occupied ? .18 : 0;
      this.light.includedOnlyMeshes = this.model?.meshes ?? this.fallback;
    }
  }

  private registerCasters(meshes: readonly AbstractMesh[]): void {
    for (const mesh of meshes) if (mesh.getTotalVertices() > 0 && mesh.isEnabled()) {
      this.options.shadowGenerator.addShadowCaster(mesh, false); this.casters.push(mesh);
    }
  }
  private clearCasters(): void {
    for (const mesh of this.casters) this.options.shadowGenerator.removeShadowCaster(mesh, false);
    this.casters = [];
  }
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true; this.clearCasters(); this.light?.dispose(); this.light = null;
    this.model?.dispose(); this.model = null; this.mount.dispose();
    this.fallbackMaterials.forEach(m => m.dispose());
  }
}
